import { isValidElement, useId } from 'react';
import track from 'src/lib/Analytics/gtm';
import { useAppContext } from 'src/lib/Utils/Contexts/AppContext';
import Link from '../../Link';
import SvgLoader from '../../SvgLoader';
import CallToAction from '../CallToAction';
import { RichText } from '../RichText';
import { MultiLinkCardGroupProps, MultiLinkCardProps } from './types';

const LinkSvg = ({ isExternal, isPngSite }: { isExternal: boolean; isPngSite?: boolean }) => (
  <SvgLoader
    name={isExternal ? 'ExternalLink' : 'Arrow'}
    size={16}
    // @ts-ignore because using custom PNG color not in TextClasses
    color={isPngSite ? 'text-[#0074B3]' : 'text-teal-darker'}
    className="shrink-0"
  />
);

const MultiLinkCard = ({
  gradient = 'from-blue to-teal-dark',
  emphasis,
  icon,
  title,
  description,
  footerLink,
  links,
  isPngSite,
}: MultiLinkCardProps) => {
  const titleId = useId();

  const triggerLinkAnalytics =
    (analytics: MultiLinkCardProps['links'][number]['analytics']) => () => {
      // trigger analytics when click Link
      track.component(analytics);
    };

  return (
    <div className="relative flex flex-col h-full">
      {/* Gradient bar */}
      <div className={`shrink-0 h-6 bg-gradient-to-r ${gradient} rounded-t-md`}></div>

      <div
        className={`grow relative isolate flex flex-col ${
          emphasis === 'subtle' ? 'bg-[#fafafa]' : 'bg-white'
        } border border-t-0 border-gray rounded-b-md overflow-clip`}
      >
        <header className="shrink-0 px-16 md:px-24 pt-16 md:pt-24">
          <div className="pb-16 border-b border-gray">
            {/* Icon */}
            {typeof icon === 'string' && (
              <img className="shrink-0 size-64 md:size-80 mb-16" src={icon} alt="" />
            )}
            {typeof icon !== 'string' && isValidElement(icon) && icon}

            {/* Title (required) */}
            <h3 className="text-xl text-gray-darker" id={titleId}>
              {title}
            </h3>

            {/* Description */}
            {!!description && <RichText value={description} tag="p" className="mt-8" />}
          </div>
        </header>

        {/* <!-- Content --> */}
        <div className="grow p-16 md:p-24">
          {/* <!-- Link list --> */}
          <ul className="space-y-16" aria-labelledby={titleId}>
            {links.map((linkData, i) => {
              const { link, hideLink, description, analytics } = linkData;
              if (hideLink) {
                return null;
              }
              return (
                <li key={i}>
                  <div className="relative isolate flex gap-x-16 items-center">
                    <div className="flex flex-col grow">
                      <p>
                        <Link
                          value={link}
                          className={`${
                            isPngSite ? 'text-[#0074B3]' : 'text-teal-darker'
                          } focus-visible:outline-none before:absolute before:-inset-0 after:absolute after:-z-10 after:-inset-8 md:after:-inset-x-12 after:rounded-md hover:after:bg-teal-lighter focus-visible:after:outline focus-visible:after:outline-3 focus-visible:after:outline-teal-darker after:transition-colors after:duration-250 after:ease-out`}
                          onClick={triggerLinkAnalytics(analytics)}
                        />
                      </p>
                      {!!description && (
                        <RichText tag="p" value={description} className="text-sm" />
                      )}
                    </div>
                    <LinkSvg isExternal={link.linktype === 'external'} isPngSite={isPngSite} />
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
        {/* Footer */}
        {footerLink?.href && (
          <footer className="shrink-0 px-16 md:px-24 py-16 text-center border-t border-t-gray">
            <Link
              value={footerLink}
              className={`${
                isPngSite ? 'text-[#0074B3]' : 'text-teal-darker'
              } relative inline-flex gap-x-4 items-center focus-visible:rounded-xs focus-visible:outline focus-visible:outline-3 focus-visible:outline-offset-4 focus-visible:outline-teal-darker before:absolute before:-inset-4`}
            >
              <span className="underline hover:no-underline">{footerLink.text}</span>
              <LinkSvg isExternal={footerLink.linktype === 'external'} isPngSite={isPngSite} />
            </Link>
          </footer>
        )}
      </div>
    </div>
  );
};

const MultiLinkCardGroup = ({ items, title, emphasis, CTA }: MultiLinkCardGroupProps) => {
  const { status } = useAppContext();
  const isPngSite =
    status.site?.name === 'jsspng' || status.route?.templateName === 'JSS PNG App Route';

  if (!items?.length) return null;

  return (
    <CallToAction {...CTA}>
      <div className="container-4xl">
        {!!title && <h2 className="text-center text-2xl mb-24">{title}</h2>}

        <ul className="group flex flex-col sm:flex-row sm:justify-center sm:flex-wrap sm:items-stretch -m-8 md:-m-12">
          {items.map((item, i) => {
            let gradientClasses = 'from-blue to-teal-dark';
            if (i === 1) {
              gradientClasses = 'from-teal-dark to-[#3BBB96]';
            } else if (i === 2) {
              gradientClasses = 'from-[#3BBB96] to-teal-dark';
            } else if (i === 3) {
              gradientClasses = 'from-teal-dark to-blue';
            }

            if (item.hideItem || !item.links?.length) {
              return null;
            }

            return (
              <li
                key={`mlcard-${i}`}
                data-testid="multi-link-card"
                className="p-8 sm:p-12 sm:w-1/2 lg:w-1/3 group-has-[>_:nth-child(4):last-child]:xl:w-1/4 lg:group-has-[>_:nth-child(4):last-child]:[&:nth-child(3)]:ms-px xl:group-has-[>_:nth-child(4):last-child]:[&:nth-child(3)]:ms-0"
              >
                <MultiLinkCard gradient={gradientClasses} {...{ ...item, emphasis, isPngSite }} />
              </li>
            );
          })}
        </ul>
      </div>
    </CallToAction>
  );
};

export default MultiLinkCardGroup;
export { MultiLinkCard };
